import { useQuery } from '@tanstack/react-query';
import { HttpLinkTemplate } from '@lib2/model/link/LinkTypes';
import { LinkApi } from './LinkApi';
import { LINK_QUERY_KEYS } from '@/lib/constants/link';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { SharedLinkConfig } from '@/lib/types/link';

/**
 * Fetch and cache shared link config for current customer.
 * Call once at parent component level, use LinkUtils for URL building.
 *
 * @example
 * ```tsx
 * function MediaGrid({ videos }) {
 *   const { data: linkConfig, isSuccess } = useSharedLinkConfig();
 *   if (!isSuccess) return <Loading />;
 *
 *   return videos.map(v => (
 *     <MediaRow
 *       thumbnailUrl={LinkUtils.getMediaFileUrl(linkConfig, v.mediaId, v.thumbFileId, 'jpg')}
 *     />
 *   ));
 * }
 * ```
 */
export function useSharedLinkConfig() {
  const { selectedCId } = useAuth();

  return useQuery({
    queryKey: LINK_QUERY_KEYS.sharedConfig(selectedCId ?? 0),
    queryFn: async (): Promise<SharedLinkConfig> => {
      const json = await LinkApi.getSharedLinkConfig(selectedCId!);
      // Transform JSON to HttpLinkTemplate instances (reusing lib2 classes)
      return {
        accountHttpTemplate: new HttpLinkTemplate(json.accountHttpTemplate),
        mediaFileRSHttpTemplate: new HttpLinkTemplate(json.mediaFileRSHttpTemplate),
        parentAccountHttpTemplate: new HttpLinkTemplate(json.parentAccountHttpTemplate),
        playerHttpTemplate: new HttpLinkTemplate(json.playerHttpTemplate),
      };
    },
    enabled: selectedCId != null,
    staleTime: 5 * 60 * 1000, // 5 min - templates change rarely
    refetchOnWindowFocus: true, // Refresh tokens when user returns
  });
}
